import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { SortTool } from '../../common/react/components/SortTool';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { QueryString } from '../../common/react/QueryString';
import { SorterActions } from '../../common/react/SorterActions';
import { ContentSortOptions } from '../../ts/models/ContentSortOptions';
import { PlaceName } from '../../ts/models/IPlace';
import { IViewDetailInfo, IViewInfo } from '../../ts/models/IViewInfo';
import { OrderKeys } from '../../ts/models/OrderKeys';
import { IActionButton } from '../../ts/services/ActionPanelService';

export module ViewGrid {

  export type ViewGridScope =
      CommonDataGridColumns.ISelectColumnScope<IViewInfo> &
      CommonDataGridColumns.IFavoriteColumnScope<IViewInfo>;

  export function columns(
      // Dependencies
      translate: IReactTranslateService,
      scope: ViewGridScope,

      // Other params
      siteUrls: SiteUrls,
      actions: IActionButton<IViewInfo, IViewDetailInfo>[],
      getActionTarget: () => IActionTarget<IViewInfo, IViewDetailInfo>,
      anyActionsAllowed: boolean,
      inProjectPlace: boolean,
      inWorkbookPlace: boolean,
      sorterActions: SorterActions,
      queryString: QueryString)
  : DataGrid.Column[] {

    const nameColumn: DataGrid.Column = CommonDataGridColumns.withExtraSpace({
      name: 'name-col',
      dataKey: 'name',
      headerText: translate('column_name'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        return {
          text: row.data.name,
          url: siteUrls.viewUrl(row.data.path),
          iconClassName: 'tb-grid-icon-view',
          iconTitle: translate('view')
        };
      })
    });

    const nameColumnWithActions = CommonDataGridColumns.withActions(nameColumn, {
      actions: actions,
      getActionTarget: getActionTarget,
      translate: translate
    }, scope);

    const hitsColumn = CommonDataGridColumns.selectableColumnWithBars('hitsCol',
      ['hitsTotal', 'hitsLastTwelveMonthsTotal', 'hitsLastThreeMonthsTotal', 'hitsLastOneMonthTotal'],
      queryString, translate, sorterActions, 'hitsTotal');

    const workbookNameColumn: DataGrid.Column = {
      name: 'workbook-name-col',
      dataKey: 'workbookName',
      headerText: translate('column_workbook'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        return {
          text: row.data.workbook.name,
          url: siteUrls.workbookViewsUrl(row.data.workbook.id)
        };
      })
    };

    const indexColumn: DataGrid.Column = {
      name: 'index-col',
      dataKey: 'index',
      headerText: translate('column_sheetNumber'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('index')),
      direction: 'asc',
    };

    const projectNameColumn: DataGrid.Column = {
      name: 'project-name-col',
      dataKey: 'projectName',
      headerText: translate('column_project'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        return {
          text: row.data.workbook.project.name,
          url: siteUrls.projectViewsUrl(row.data.workbook.project.id)
        };
      })
    };

    const ownerColumn = CommonDataGridColumns.owner(translate, siteUrls,
      (row: DataGrid.Row) => row.data.workbook.owner);

    const updatedAtColumn: DataGrid.Column = {
      name: 'updated-at-col',
      dataKey: 'updatedAt',
      headerText: translate('column_updated'),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('updatedAt')
    };

    return _.compact([
      anyActionsAllowed && CommonDataGridColumns.selected(scope),
      CommonDataGridColumns.favorite(scope),
      anyActionsAllowed ? nameColumnWithActions : nameColumn,
      hitsColumn,
      !inWorkbookPlace && workbookNameColumn,
      indexColumn,
      !inWorkbookPlace && !inProjectPlace && projectNameColumn,
      !inWorkbookPlace && ownerColumn,
      !inWorkbookPlace && updatedAtColumn
    ]);
  }

  export function sortOptions(place: PlaceName, hasSearchFilter: boolean): SortTool.SortOption[] {
    return ContentSortOptions.sortOptionsForValidKeys([
      hasSearchFilter && OrderKeys.relevance,
      OrderKeys.name,
      OrderKeys.hitsTotal,
      OrderKeys.hitsLastTwelveMonthsTotal,
      OrderKeys.hitsLastThreeMonthsTotal,
      OrderKeys.hitsLastOneMonthTotal,
      place !== 'workbook' && OrderKeys.workbookName,
      OrderKeys.index,
      place !== 'workbook' && place !== 'project' && OrderKeys.projectName,
      place !== 'workbook' && OrderKeys.ownerName,
      place !== 'workbook' && OrderKeys.updatedAt
    ]);
  }

}
